/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
/* eslint-disable @typescript-eslint/ban-ts-comment */
'use client'

import { Listbox, Transition } from '@headlessui/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Fragment, Suspense, useMemo } from 'react'

import { Button } from '@/components/library/button'
import { isNullOrEmpty } from '@/utils/string-extensions'
import Link from 'next/link'
/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useGetRedirectPath } from '@/components/utility/product-filters/product-filters'

type SectionHeaderType = 'seeAll' | 'filterOptions' | 'empty'
export enum SectionHeaderTypes {
  seeAll = 'seeAll',
  filterOptions = 'filterOptions',
  empty = 'empty',
}

export interface SeeAllButtonProps {
  seeAllLink: string | undefined
  seeAllTitle: string | undefined
}

export interface FilterOptionsProps {
  defaultSortBy?: string
  showBuyNowOnly?: boolean
  enableBuyNowOnly?: boolean
}

export interface TitleRowProps {
  type: SectionHeaderType | SectionHeaderTypes
}

export interface HeaderTypeProps {
  seeAllLink?: string
  seeAllTitle?: string
  type: SectionHeaderType | SectionHeaderTypes
  defaultSortBy?: string
  showBuyNowOnly?: boolean
  enableBuyNowOnly?: boolean
  className?: string
}
export interface SectionHeaderProps extends HeaderTypeProps {
  title?: string
  headingVariant?: string
  productCountSuffix?: string
  seeAllNewPageTitle?: string
}

const SeeAllButton = ({ seeAllLink, seeAllTitle }: SeeAllButtonProps) => {
  if (!seeAllLink) {
    return null
  }
  return (
    <Link prefetch={false} href={seeAllLink}>
      <Button
        variant='outlined'
        size='small'
        color='secondary'
        data-testid='SeeAll'
      >
        {seeAllTitle}
      </Button>
    </Link>
  )
}

const sortByOptions: FilterOption[] = [
  {
    label: 'Lowest to Highest',
    value: 'product_salePrice_asc',
  },
  {
    label: 'Highest to lowest',
    value: 'product_salePrice_desc',
  },
  { label: 'Best Match', value: 'best_match' },
]

interface FilterOption {
  label: string
  value: string
}

const FilterOptions = ({
  defaultSortBy,
  enableBuyNowOnly = true,
}: FilterOptionsProps) => {
  const router = useRouter()
  const { getRedirectPath } = useGetRedirectPath()
  const handleSortByChange = (value: FilterOption) => {
    const redirectPath = getRedirectPath([value.value], 'sortBy')
    router.push(redirectPath)
  }
  const sortBy = useSearchParams().get('sortBy') ?? 'best_match'

  const selected = useMemo(() => {
    return sortByOptions.find((option) => option.value === sortBy)
  }, [sortBy])

  return (
    <div className='ml-6 mr-10 flex min-w-max flex-row gap-4 sm:ml-0'>
      <div className='mx-0 my-auto flex text-center'>
        <span className='mx-1 my-auto flex-1 text-xs font-bold'>Sort by:</span>
        <Listbox
          value={selected}
          onChange={(value) => {
            handleSortByChange(value)
          }}
        >
          <Listbox.Button
            className='h-[14px] text-xs underline'
            title='Sort By'
          >
            {selected?.label}
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options className='absolute z-50 mt-1 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none'>
              {sortByOptions.map((option) => (
                // @ts-ignore - union is fine
                <Listbox.Option
                  key={option.label}
                  value={option}
                  className='cursor-pointer p-2 text-left font-normal text-dark'
                >
                  {option.label}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </Listbox>
      </div>
    </div>
  )
}

const HeaderType = ({
  type,
  seeAllTitle = 'See All',
  seeAllLink,
  defaultSortBy,
  showBuyNowOnly,
  enableBuyNowOnly,
}: HeaderTypeProps) => {
  switch (type) {
    case SectionHeaderTypes.seeAll:
      return <SeeAllButton seeAllTitle={seeAllTitle} seeAllLink={seeAllLink} />

    case SectionHeaderTypes.filterOptions:
      return (
        <Suspense fallback={null}>
          <FilterOptions
            defaultSortBy={defaultSortBy}
            showBuyNowOnly={showBuyNowOnly}
            enableBuyNowOnly={enableBuyNowOnly}
          />
        </Suspense>
      )

    default:
      return null
  }
}

export const SectionHeader = ({
  defaultSortBy,
  showBuyNowOnly,
  title,
  seeAllLink,
  seeAllTitle,
  type,
  enableBuyNowOnly,
}: SectionHeaderProps) => (
  <div
    className={`${
      type === SectionHeaderTypes.empty ? 'min-h-0' : 'min-h-[40px]'
    } w-full ${
      type === SectionHeaderTypes.filterOptions ? 'inline-block' : 'flex'
    } sm:flex`}
  >
    <div
      className='mx-0 my-1 min-w-max flex-1 pb-4 pl-6 text-h3 leading-h3 sm:pb-0 sm:pl-0 md:text-h2 md:leading-h2 '
      data-testid='infinite-scroll-grid-title'
    >
      {!isNullOrEmpty(title) && (
        <h1 className='text-bold text-h2 leading-6'>{title}</h1>
      )}
    </div>
    <div className='hidden'>
      <HeaderType
        type={type}
        seeAllLink={seeAllLink}
        seeAllTitle={seeAllTitle}
        defaultSortBy={defaultSortBy}
        showBuyNowOnly={showBuyNowOnly}
        enableBuyNowOnly={enableBuyNowOnly}
      />
    </div>
  </div>
)
